import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { submitContactForm } from '../mail';
import { useToast } from '../hooks/use-toast';
import countryCodes from './countryCodes.json';

const Contact = () => {
  const { toast } = useToast();
  const defaultDial = countryCodes.find(c => c.code === 'AE')?.dial_code || '+971';

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phoneCountry: defaultDial,
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Validate form on submit
    const newErrors = {};

    // Email validation
    const email = (formData.email || '').trim();
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!email) {
      newErrors.email = 'Email is required.';
    } else if (email.length > 100) {
      newErrors.email = 'Email must be at most 100 characters.';
    } else if (!emailPattern.test(email)) {
      newErrors.email = 'Enter a valid email address.';
    }

    // Phone validation (local part)
    const phone = (formData.phone || '').trim();
    const phonePattern = /^[0-9\s\-\+()]*$/;
    if (!phone) {
      newErrors.phone = 'Phone number is required.';
    } else if (phone.length > 100) {
      newErrors.phone = 'Phone number must be at most 100 characters.';
    } else if (!phonePattern.test(phone)) {
      newErrors.phone = 'Phone number contains invalid characters.';
    }

    // Message validation
    const message = (formData.message || '').trim();
    if (!message) {
      newErrors.message = 'Message is required.';
    } else if (message.length > 1000) {
      newErrors.message = 'Message must be at most 1000 characters.';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setIsSubmitting(false);
      return;
    }

    setErrors({});

    try {
      const payload = {
        ...formData,
        // combine country code + local number for submission
        phone: `${formData.phoneCountry} ${formData.phone}`.trim()
      };

      const result = await submitContactForm(payload);
      if (result.success) {
        toast({
          title: 'Message Sent!',
          description: 'Thank you for contacting us. We will get back to you soon.',
        });
        setFormData({
          name: '',
          email: '',
          phoneCountry: defaultDial,
          phone: '',
          message: ''
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Something went wrong. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // sort country list alphabetically for the dropdown
  const sortedCountries = [...countryCodes].sort((a, b) => a.name.localeCompare(b.name));

  // Google Maps link for the address
  const googleMapsUrl = `https://maps.app.goo.gl/YiFSbxr57ESt3gYM6`;
  
  return (
    <section id="contact" className="py-20 sm:py-28 bg-blue-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Get In Touch
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            Have questions about our products or need a quote?
            Reach out to us and we'll respond as soon as possible.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Information */}
          <div className="space-y-8">
            <div className="bg-slate-50 p-8 rounded-xl">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Contact Information</h3>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="text-blue-600" size={24} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Phone</h4>
                    <p className="text-gray-600">Sales Team</p>
                    <a href="tel:+971586712077" className="text-gray-600 hover:text-blue-600 underline transition-colors">+971 58 671 2077</a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="text-blue-600" size={24} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Email</h4>
                    <p className="text-gray-600">info@famparts.com</p>
                  </div>
                </div>

                {/* Address — clickable, opens Google Maps */}
                <div>
                  <a
                    href={googleMapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-4 group"
                  >
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="text-blue-600 group-hover:text-blue-800" size={24} />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Address</h4>
                      <p className="text-gray-600 underline group-hover:text-blue-600">
                        Opp. Unipex Factory, Industrial Area 2 <br />
                        Sharjah, United Arab Emirates
                      </p>
                    </div>
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 p-6 rounded-xl border-2 border-blue-200">
              <h4 className="font-semibold text-gray-900 mb-2">Business Hours</h4>
              <p className="text-gray-600">Saturday - Thursday: 9:00 AM - 7:00 PM</p>
              <p className="text-gray-600">Friday: Closed</p>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-slate-50 p-8 rounded-xl">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Send Us a Message</h3>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  maxLength={100}
                  className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none transition-colors"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  maxLength={100}
                  pattern="[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}"
                  className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none transition-colors"
                  placeholder="john@example.com"
                  title="Please enter a valid email address (max 100 characters)"
                />
                {errors.email && (
                  <p className="text-xs text-red-600 mt-1">{errors.email}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Phone Number *
                </label>

                <div className="flex gap-3">
                  <div className="relative w-20 sm:w-24 h-13">
                    {/* visible compact display (shows only the dial code) */}
                    <div
                      className="flex items-center h-full px-4 bg-white border-2 border-gray-200 rounded-lg pointer-events-none text-sm"
                      aria-hidden="true"
                    >
                      <span className="text-sm font-medium mr-1">{formData.phoneCountry}</span>
                      <svg
                        className="w-3 h-3 text-gray-500"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                      >
                        <path d="M6 8l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>

                    {/* native select overlaid (invisible) — opens dropdown showing full names */}
                    <select
                      name="phoneCountry"
                      value={formData.phoneCountry}
                      onChange={handleChange}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-20"
                      aria-label="Country code"
                    >
                      {sortedCountries.map((c) => (
                        <option key={c.code} value={c.dial_code}>
                          {c.name} ({c.dial_code})
                        </option>
                      ))}
                    </select>
                  </div>

                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    maxLength={100}
                    pattern="[\d\s\-\+\(\)]*"
                    className="flex-1 px-4 py-3 bg-white border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none transition-colors"
                    placeholder="56 123 4567"
                    required
                    title="Phone number can contain digits, spaces, hyphens, plus sign, and parentheses only (max 100 characters)"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2">Select country code then enter local number (no need to repeat code).</p>
                {errors.phone && (
                  <p className="text-xs text-red-600 mt-1">{errors.phone}</p>
                )}
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  maxLength={1000}
                  rows={5}
                  className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none transition-colors resize-none"
                  placeholder="Tell us about the parts you need..."
                />
                <p className="text-xs text-gray-500 mt-2">Message must be between 1 and 1000 characters</p>
                {errors.message && (
                  <p className="text-xs text-red-600 mt-1">{errors.message}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-6 py-4 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
                <Send size={20} />
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;